import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, IconButton, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteService, listServicesAdmin } from '../../api/services';
import { useApiCallWithAuth } from '../../hooks/useApiCallWithAuth';
import { ServiceItem } from '../../types/service';

function formatPrice(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

export function ServicesAdminPage() {
  const navigate = useNavigate();
  const { request: listRequest } = useApiCallWithAuth(listServicesAdmin);
  const { request: deleteRequest } = useApiCallWithAuth(deleteService);

  const [services, setServices] = useState<ServiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ServiceItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  function loadServices() {
    setLoading(true);
    listRequest()
      .then(setServices)
      .catch(() => setError('Failed to load services'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadServices(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteRequest(deleteTarget.serviceGuid);
      setDeleteTarget(null);
      loadServices();
    } catch {
      setError('Failed to delete service');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Box sx={{ pt: 3 }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 2 }}>
        <Typography variant='h5' fontWeight={700}>Services Admin</Typography>
        <Button variant='contained' startIcon={<AddIcon />} onClick={() => navigate('/services/new')}>
          New Service
        </Button>
      </Stack>

      {loading && <CircularProgress size={24} />}
      {error && <Typography color='error' sx={{ mb: 2 }}>{error}</Typography>}

      {!loading && !error && services.length === 0 && (
        <Typography color='text.secondary'>No services yet.</Typography>
      )}

      {!loading && services.length > 0 && (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Price</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align='right'><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {services.map(service => (
              <TableRow key={service.serviceGuid} hover>
                <TableCell>
                  <Typography variant='body2'>{service.name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant='body2'>{formatPrice(service.priceCents)}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={service.isActive ? 'Active' : 'Inactive'}
                    size='small'
                    color={service.isActive ? 'success' : 'default'}
                    variant={service.isActive ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell align='right'>
                  <Stack direction='row' spacing={0.5} justifyContent='flex-end'>
                    <Tooltip title='Edit'>
                      <IconButton size='small' onClick={() => navigate(`/services/${service.serviceGuid}/edit`)}>
                        <EditIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Delete'>
                      <IconButton size='small' color='error' onClick={() => setDeleteTarget(service)}>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete service?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            &quot;{deleteTarget?.name}&quot; will be permanently deleted. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color='error' variant='contained' disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
